/**
 * Puzzle 4 — Shadow Relay.
 *
 * Basil holds a pressure plate (ideally with Pneumatic Anchor) to open a
 * gate. Minna uses Shadow Slip to sneak past a patrolling fox, then her
 * Calming Resonance charges the relay crystal.
 *
 * Both cats are required: the relay plate only answers to Basil's weight,
 * and the relay crystal only takes Minna's charge once Basil has armed the
 * plate. Before, the crystal charged whenever Minna purred at it and the
 * latching plate took either cat, so Minna could solve the relay alone
 * (tools/playtest/scenarios/solo-solve.mjs).
 */
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";

import { PressurePlate } from "../../../entities/interactives/PressurePlate";
import { CrystalReceiver } from "../../../entities/interactives/CrystalReceiver";
import { Fox } from "../../../entities/npcs/Fox";

import { GameEvents } from "../../../config/events";
import { COLORS } from "../../../config/GameConfig";
import { tagAs } from "../../PlaceholderTag";

import type { PuzzleContext, PuzzleCue, PuzzleModule, PuzzleState } from "../../PuzzleModule";
import type { CueDef } from "../../../ui/tutorialCues";
import type { PuzzleQAHints } from "../../qa/PuzzleQAHints";

const CUE = {
  plate: { cat: "basil", parts: [{ text: "onto the plate," }, { action: "anchor" }, { text: "anchor" }] },
  slip: { cat: "minna", parts: [{ action: "shadow" }, { text: "slip past the fox" }] },
  charge: { cat: "minna", parts: [{ action: "purr", hold: true }, { text: "charge the crystal" }] },
} satisfies Record<string, CueDef>;
const OBJECTIVE = "Charge the shadow relay";
/** Minna this close to the crystal is past the fox: cue the purr. */
const NEAR_CRYSTAL = 5;

export class ShadowRelayPuzzle implements PuzzleModule {
  readonly id = "shadowRelay";
  readonly displayName = "Shadow Relay";

  private ctx!: PuzzleContext;

  private relayPlate!: PressurePlate;
  private relayGate!: Mesh;
  private relayGateMat!: PBRMaterial;
  private relayCrystal!: CrystalReceiver;
  private relayFox!: Fox;

  private platePressed = false;
  private relayComplete = false;

  // Track the plate-holding cat so we can surface a "anchor Basil first"
  // hint when the player tabs away in solo mode without engaging
  // PneumaticAnchor. The L3 QA pass flagged this as a silent solo-mode
  // trap — the gate would slam shut the moment the inactive cat walked
  // off the plate, with no explanation.
  private plateHolderCat: "minna" | "basil" | null = null;
  private anchorHintShown = false;
  private minnaTooLightHintShown = false;
  private unarmedCrystalHintShown = false;

  setup(ctx: PuzzleContext): void {
    this.ctx = ctx;
    const { scene } = ctx;

    // ── Pressure plate ──
    this.relayPlate = new PressurePlate(scene, {
      key: "relayPlate",
      position: new Vector3(85, 2.2, 0),
      label: "Relay Pressure Plate",
    });

    // ── Gate (opens when plate is held) ──
    this.relayGate = MeshBuilder.CreateBox(
      "relayGate",
      { width: 0.5, height: 4, depth: 2 },
      scene,
    );
    this.relayGate.position = new Vector3(90, 4, 0);
    this.relayGateMat = new PBRMaterial("gateMat", scene);
    this.relayGateMat.albedoColor = new Color3(0.3, 0.2, 0.15);
    this.relayGateMat.metallic = 0.5;
    this.relayGateMat.roughness = 0.5;
    this.relayGate.material = this.relayGateMat;
    tagAs(this.relayGate, "placeholder");

    // ── Relay crystal (Minna's target) ──
    this.relayCrystal = new CrystalReceiver(scene, {
      key: "relayCrystal",
      position: new Vector3(105, 4.5, 0),
      hidden: false,
      label: "Relay Crystal",
    });

    // ── Fox patrol ──
    // L3 tightens the cone per SPEC-fox-sight-cone.md §5 — a narrower
    // patrol angle + slightly shorter radius matches the Shadow Relay
    // puzzle's "sneak past one focused patrol" design. The visualiser
    // must never lie to the player, so the half-angles here are purely
    // visual; the detection radius remains whatever `Fox.canDetect()`
    // evaluates against `NPC_TUNING.FOX.ALERT_RADIUS`.
    this.relayFox = new Fox(scene, "canopyFox1", new Vector3(95, 2, 0), [92, 103], {
      patrolHalfAngle: Math.PI * 0.2, // ~36°
      alertHalfAngle: Math.PI * 0.32, // ~58°
      huntHalfAngle: Math.PI * 0.1,   // ~18°
      radius: 6.5,
    });
    // Foxes construct in the dormant "blinded" phase (they wait for L2's
    // Incubation flip). The Canopy has no cycle, and nothing ever woke
    // this fox — it patrolled with `canDetect` permanently false, so the
    // whole "sneak past the fox" beat had no teeth and Shadow Slip was
    // never actually needed. Wake it for good; the startle from a
    // Chronos Bubble is the intended counterplay for Basil.
    this.relayFox.enterHunting();

    ctx.registerInteractive({
      mesh: this.relayPlate.rootMesh,
      hidden: false,
      state: "revealed",
    });
    ctx.registerInteractive({
      mesh: this.relayCrystal.rootMesh,
      hidden: false,
      state: "revealed",
    });

    // ── Pressure plate events ──
    // Latching plate: once pressed, gate stays open. The book (p.18)
    // describes the relay plate as a "shadow zone trigger" — single
    // activation arms the relay. Without latching, solo play is
    // impossible: Basil's plate-holding capsule blocks Minna's
    // running-jump runway, but moving Basil off the plate closes the
    // gate before she crosses. Latching preserves the canon flow
    // (Basil acts as the trigger, Minna passes the gate and reaches
    // the crystal) while letting both cats reposition freely after
    // the trigger fires.
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.PRESSURE_PLATE_ACTIVATED, (key) => {
        if (key === "relayPlate") {
          this.platePressed = true;
          this.relayGate.position.y = 12; // Move gate out of the way
        }
      }),
    );

    // ── Chronos Bubble slows the fox (Basil's counterplay) ──
    // Minna crosses the patrol with Shadow Slip; Basil can't stealth, so
    // his tool for passing the now-live fox is the L2 Elder reward.
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.CHRONOS_BUBBLE_TRIGGERED, (data) => {
        const dx = this.relayFox.mesh.position.x - data.x;
        const dy = this.relayFox.mesh.position.y - data.y;
        if (Math.sqrt(dx * dx + dy * dy) <= data.radius) {
          this.relayFox.setTimeScale(data.timeScale);
        }
      }),
    );
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.CHRONOS_BUBBLE_ENDED, () => {
        this.relayFox.setTimeScale(1);
      }),
    );

    // ── Crystal charged fires the win condition ──
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.CRYSTAL_ACTIVATED, (key) => {
        if (key === "relayCrystal" && !this.relayComplete) {
          this.relayComplete = true;
          ctx.gameEvents.emit(GameEvents.PUZZLE_SOLVED, {
            puzzleId: this.id,
          });
          ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
            message: "Shadow relay complete!",
            color: COLORS.EMERALD,
          });
        }
      }),
    );
  }

  update(dt: number, elapsed: number): void {
    const { minna, basil, calmingResonance, basilAbilities, shadowSlip } = this.ctx;

    // Pressure plate check — the relay plate only answers to Basil's
    // weight (two-cat gate, see header).
    this.relayPlate.checkPressure([basil.mesh]);
    if (
      !this.platePressed &&
      !this.minnaTooLightHintShown &&
      this.relayPlate.isOccupiedBy(minna.mesh.position)
    ) {
      this.minnaTooLightHintShown = true;
      this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
        message: "Minna is too light to trip the relay plate. It takes Basil's weight.",
      });
    }

    // Track which cat (if any) is currently holding the plate so we can
    // flag a solo-mode hazard when the player tabs away.
    if (this.platePressed) {
      const plateCenter = this.relayPlate.rootMesh.position;
      const basilDist = Vector3.Distance(basil.mesh.position, plateCenter);
      const minnaDist = Vector3.Distance(minna.mesh.position, plateCenter);
      if (basilDist < minnaDist) this.plateHolderCat = "basil";
      else this.plateHolderCat = "minna";
    }

    // One-shot solo hint: if Basil is the plate-holder AND he's not
    // anchored AND Minna has drifted more than 3 units past the gate,
    // surface a HUD toast explaining how to make the plate survive the
    // Tab swap. We only fire this once per level load.
    if (
      !this.anchorHintShown &&
      this.plateHolderCat === "basil" &&
      !this.relayComplete
    ) {
      const anchorAbility = basilAbilities.getAbility("pneumaticAnchor");
      const anchored = anchorAbility?.isActive === true;
      const minnaBeyondGate = minna.mesh.position.x > 92;
      if (!anchored && minnaBeyondGate) {
        this.anchorHintShown = true;
        this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
          message: "Anchor Basil on the plate with key 3 before you sneak Minna through — the gate stays open while he's locked down.",
        });
      }
    }

    // Fox patrol update — feed Minna's stealth probe into the sight-cone
    // visualiser so the player sees the cone "fail to bite" while she
    // walks through a shadow zone with Shadow Slip held. Detection
    // gameplay is unchanged (still routed through `fox.canDetect`).
    this.relayFox.syncSightCone(
      shadowSlip.isActive ? minna.mesh.position : null,
      shadowSlip.isActive,
    );
    this.relayFox.update(elapsed, dt);

    // Fox bite — same nip-back-to-checkpoint consequence as the L2
    // escort foxes. Shadow Slip makes Minna undetectable; Basil times
    // the patrol or slows the fox with a Chronos Bubble.
    if (!this.relayComplete) {
      const minnaStealthed = shadowSlip.isActive;
      this.maybeBite("minna", minna.mesh.position, minnaStealthed, elapsed);
      this.maybeBite("basil", basil.mesh.position, false, elapsed);
    }

    // Relay crystal: charges when Minna purrs near it, once Basil has
    // armed the relay plate.
    if (calmingResonance.isActive && !this.relayComplete) {
      const relayCrystalDist = Vector3.Distance(
        minna.mesh.position,
        this.relayCrystal.rootMesh.position,
      );
      const isPurring = relayCrystalDist <= calmingResonance.getRadius();
      if (this.platePressed) {
        this.relayCrystal.updateCharge(dt, isPurring);
      } else if (isPurring && !this.unarmedCrystalHintShown) {
        this.unarmedCrystalHintShown = true;
        this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
          message: "The relay crystal is dead. Basil has to arm the relay plate first.",
        });
      }
    }
  }

  /** Scene-time (seconds) after which the fox may bite again. */
  private nextBiteAllowedAt = 0;
  /** Distance at which the hunting fox bites a detectable cat. */
  private static readonly BITE_RADIUS = 1.4;
  /** Cooldown between bites (seconds of scene time). */
  private static readonly BITE_COOLDOWN_S = 2.5;

  private maybeBite(
    cat: "minna" | "basil",
    catPos: Vector3,
    isStealthed: boolean,
    sceneTime: number,
  ): void {
    if (sceneTime < this.nextBiteAllowedAt) return;
    // A braced Pneumatic Anchor is bite-proof: the fox's glass jaw can't
    // crack the brass harness. This keeps the canonical choreography
    // (Basil anchored on/near the plate inside the patrol) viable now
    // that the fox has teeth.
    if (cat === "basil") {
      const anchor = this.ctx.basilAbilities.getAbility("pneumaticAnchor");
      if (anchor?.isActive === true) return;
    }
    if (!this.relayFox.canDetect(catPos.x, catPos.y, isStealthed)) return;
    if (
      Vector3.Distance(this.relayFox.mesh.position, catPos) >
      ShadowRelayPuzzle.BITE_RADIUS
    ) {
      return;
    }

    this.nextBiteAllowedAt = sceneTime + ShadowRelayPuzzle.BITE_COOLDOWN_S;
    this.ctx.gameEvents.emit(GameEvents.PLAYER_DAMAGED, {
      cat,
      source: "foxBite",
      severity: "light",
    });
    // Recoil beat so the lunge reads, and the bitten cat gets a clean exit.
    this.relayFox.startleFor();

    const level = (this.ctx.scene.metadata as {
      level?: {
        respawn: (
          which: "minna" | "basil",
          target?: { x: number; y: number; z: number },
          reason?: "fall" | "fail" | "restore",
        ) => void;
      };
    }).level;
    level?.respawn(cat, undefined, "fail");
  }

  getCue(): PuzzleCue | null {
    if (this.relayComplete) return null;
    const above = (v: Vector3, dy: number) => ({ x: v.x, y: v.y + dy, z: 0 });
    if (!this.platePressed) return { objective: OBJECTIVE, cue: CUE.plate, anchor: above(this.relayPlate.rootMesh.position, 1.2) };
    const crystal = this.relayCrystal.rootMesh.position;
    if (Vector3.Distance(this.ctx.minna.mesh.position, crystal) < NEAR_CRYSTAL) {
      return { objective: OBJECTIVE, cue: CUE.charge, anchor: above(crystal, 1.2) };
    }
    return { objective: OBJECTIVE, cue: CUE.slip, anchor: above(this.relayFox.mesh.position, 1.5) };
  }

  getState(): PuzzleState {
    if (this.relayComplete) return { solved: true, progress: 1 };
    // Plate pressed is ~50% of the puzzle; the rest is the crystal charge
    return {
      solved: false,
      progress: this.platePressed ? 0.5 : 0,
    };
  }

  getQAHints(): PuzzleQAHints {
    return {
      puzzleId: this.id,
      successEvent: GameEvents.PUZZLE_SOLVED,
      targets: [
        { id: "relayPlate",   position: { x: 85, y: 2.2 }, radius: 1.2, allowedCat: "either", label: "Relay pressure plate (shadow zone)" },
        { id: "relayGate",    position: { x: 90, y: 4 },   radius: 1.5, allowedCat: "either", label: "Relay gate" },
        { id: "relayCrystal", position: { x: 105, y: 4.5 }, radius: 1.2, allowedCat: "minna", label: "Relay crystal (Purr-charge)" },
        { id: "fox_patrol",   position: { x: 95, y: 2 },  radius: 2.0, allowedCat: "either", label: "Canopy fox patrol midpoint" },
        { id: "plat_s4_2_west", position: { x: 91, y: 2.2 }, radius: 1.5, allowedCat: "either", label: "plat_s4_2 west landing" },
        { id: "plat_s4_2_east", position: { x: 99, y: 2.2 }, radius: 1.0, allowedCat: "either", label: "plat_s4_2 east end (running-jump launch to plat_s4_3)" },
        { id: "plat_s4_2_mid", position: { x: 95, y: 2.2 }, radius: 5.0, allowedCat: "either", label: "anywhere on plat_s4_2 top" },
        { id: "plat_s4_3_top",  position: { x: 105, y: 4.2 }, radius: 2.5, allowedCat: "either", label: "plat_s4_3 top (under relay crystal)" },
      ],
      traversals: [
        // Running jump from plat_s4_1 east edge (x=85) east across the
        // 5u gap to plat_s4_2 (x=[90, 100], top y=2.2). The auto-
        // navigator's launch timing on this gap is flaky (drops cats
        // into the void on certain seeds — observed at seed 1234).
        // Hand-authored recipe with 800ms pre-jump walk so cat hits
        // launch x with full vx=MOVE_SPEED=5, clearing 5u airtime.
        {
          id: "running_jump_plat_s4_1_to_plat_s4_2",
          kind: "jump",
          cat: "either",
          from: "relayPlate",
          to: "plat_s4_2_west",
          successWindowMs: 4_000,
          recipe: [
            { keyDown: "d" },
            { waitMs: 800 },
            { keyDown: " " },
            { waitMs: 80 },
            { keyUp: " " },
            { waitMs: 1_000 },
            { keyUp: "d" },
          ],
        },
        // Running jump from plat_s4_2 east edge (x=100) up to plat_s4_3
        // (x=[102.5, 107.5] top y=4.2). Δy=+2u, gap=2.5u — the canonical
        // path for Minna to reach the relay crystal at (105, 4.5).
        // Recipe: walk east 600ms (hits plat_s4_2 east edge at full
        // MOVE_SPEED=5), jump (vy=9 peak rise 2.7u clears Δy=2),
        // continue east 700ms for east drift across the 2.5u gap.
        {
          id: "running_jump_plat_s4_2_to_plat_s4_3",
          kind: "jump",
          cat: "either",
          from: "plat_s4_2_east",
          to: "plat_s4_3_top",
          successWindowMs: 4_000,
          recipe: [
            { keyDown: "d" },
            { waitMs: 600 },
            { keyDown: " " },
            { waitMs: 80 },
            { keyUp: " " },
            { waitMs: 700 },
            { keyUp: "d" },
          ],
        },
        // Short-runway running jump for Basil starting at the relay
        // plate (x=84.5). Plat_s4_1 east edge at x=85 — only 0.5u of
        // runway before basil walks off. Recipe matches the working
        // sequence from the L3-shadowRelay plan: brief walk-up east,
        // RELEASE "a" before jump (so vx is low at jump time, basil
        // jumps mostly vertically from near plat edge), then re-hold
        // "a" post-jump for AIR_ACCEL to ramp vx and carry basil
        // east to plat_s4_2.
        {
          id: "running_jump_plate_to_plat_s4_2",
          kind: "jump",
          cat: "basil",
          from: "relayPlate",
          to: "plat_s4_2_west",
          successWindowMs: 4_000,
          recipe: [
            { key: "d", holdMs: 200 },
            { key: " ", holdMs: 80, delayMs: 0 },
            { key: "d", holdMs: 1_000, delayMs: 0 },
          ],
        },
        // Walk-east a short distance on plat_s4_2 to clear a partner
        // cat's landing zone. 800ms hold at MOVE_SPEED=5 covers ~3u
        // east — enough for the leading cat to step out of the way
        // without triggering full sep-clamp damping.
        {
          id: "walk_east_plat_s4_2_short",
          kind: "ability",
          cat: "either",
          from: "plat_s4_2_west",
          to: "plat_s4_2_mid",
          successWindowMs: 2_000,
          recipe: [
            { keyDown: "d" },
            { waitMs: 800 },
            { keyUp: "d" },
          ],
        },
      ],
    };
  }

  /**
   * Sub-puzzle state seeder. Shape:
   *   {
   *     platePressed?: boolean,   // raise the gate (plate-held state)
   *     solved?: boolean,         // charge the crystal fully + emit PUZZLE_SOLVED
   *   }
   *
   * `{ platePressed: true }` stages the "gate is up, now sneak Minna
   * past the fox" test without tying up the inactive cat. `{ solved: true }`
   * skips the entire fox-stealth sub-game for traces testing downstream
   * apex gating (`theOrrery` reads `shadowRelay.solved`).
   */
  seed(state: Record<string, unknown>): void {
    if (state.platePressed === true && !this.platePressed) {
      this.platePressed = true;
      this.relayGate.position.y = 12;
    }
    if (state.solved === true && !this.relayComplete) {
      // Burn charge into the crystal the same way a sustained Purr would —
      // `updateCharge(large dt, true)` tips it into the `solveCrystal`
      // branch which emits CRYSTAL_ACTIVATED, and our own handler flips
      // `relayComplete` + emits PUZZLE_SOLVED. Single code path for both
      // real-play and seeded-play.
      this.relayCrystal.updateCharge(10, true);
      if (!this.relayComplete) {
        // Defensive fallback if the charge path short-circuits — still
        // emit PUZZLE_SOLVED so levelState is consistent.
        this.relayComplete = true;
        this.ctx.gameEvents.emit(GameEvents.PUZZLE_SOLVED, { puzzleId: this.id });
      }
    }
  }

  dispose(): void {
    this.relayPlate.dispose();
    this.relayGate.dispose();
    this.relayGateMat.dispose();
    this.relayCrystal.dispose();
    this.relayFox.dispose();
  }
}
